import React, { useEffect } from "react";
import ListSelect from "../ListSelect/ListSelect";
import {
  Avatar,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  Tooltip,
  Alert,
} from "@mui/material";
import { FaFileExport } from "react-icons/fa";
import RadioButtonsGroup from "../RadioOptions/RadioOptions";
import { exportFileToDownload } from "../../api";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { fetchBoardName } from "../../redux/reducers/trello/slice";
import DownloadIcon from "@mui/icons-material/Download";

const MotionGrid = motion(Grid);

export default function ExportTab() {
  const dispatch = useDispatch();
  const [listName, setListName] = React.useState([]);
  const [format, setFormat] = React.useState("XLS");
  const [loading, setLoading] = React.useState(false);
  const [base64, setBase64] = React.useState("");

  const { cards, boardName, boardId, email } = useSelector(
    (state) => state.trello
  );
  const hasActivePlan = useSelector((state) => state.user.isAuth);

  const handleExport = async () => {
    const data = {
      textEmail: email,
      textBoardId: boardId,
      nameBoard: boardName,
      typeExportacao: format === "XLS" ? "xlsx" : "csv",
      body: listName,
    };

    try {
      setLoading(true);
      const response = await exportFileToDownload(data);
      if (response && typeof response === "string") {
        setBase64(response);
      }
    } catch (error) {
      console.error("Error exporting file:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!boardName) dispatch(fetchBoardName());
  }, []);

  useEffect(() => {
    setBase64("");
  }, [listName, format]);

  const downloadBase64File = (base64Data, fileName, mimeType) => {
    const prefix =
      mimeType === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "text/csv";

    const link = document.createElement("a");
    link.href = `data:${prefix};base64,${base64Data}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportDisabled =
    !hasActivePlan ||
    listName.length === 0 ||
    format === "" ||
    cards.length === 0 ||
    loading;

  return (
    <MotionGrid
      initial={{ x: "100vw" }}
      animate={{
        x: 0,
        transition: { type: "spring", mass: 0.4, damping: 8 },
      }}
      exit={{ opacity: 0, scale: 0.5, transition: { delay: 0.1 } }}
      container
      mt={5}
    >
      {/* Mensagem de plano expirado */}
      {!hasActivePlan && (
        <Grid item xs={12} sx={{ mb: 3 }}>
          <Alert
            severity="warning"
            variant="filled"
            sx={{
              display: "flex",
              alignItems: "center",
              "& .MuiAlert-message": {
                display: "flex",
                alignItems: "center",
                padding: 0,
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, ml: 1 }}>
              You have no active plans and your trial period has ended.
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Seletor de listas */}
      <Grid item xs={5.5}>
        <Grid
          item
          xs={12}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Avatar sx={{ bgcolor: "#008c23", mr: 0.5 }} alt="Remy Sharp">
            1
          </Avatar>
          <Typography variant="h6" gutterBottom>
            Choose 1 or more lists
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <ListSelect listName={listName} setListName={setListName} />
        </Grid>
      </Grid>

      <Grid
        item
        xs={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Divider
          sx={{ height: "100%", background: "#008c2330" }}
          orientation="vertical"
          flexItem
        />
      </Grid>

      {/* Seletor de formato */}
      <Grid item xs={5.5}>
        <Grid
          item
          xs={12}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Avatar sx={{ bgcolor: "#008c23", mr: 0.5 }} alt="Remy Sharp">
            2
          </Avatar>
          <Typography variant="h6" gutterBottom>
            Choose the file format
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <RadioButtonsGroup setFormat={setFormat} />
        </Grid>
      </Grid>

      {/* Botões de ação */}
      <Grid
        item
        xs={12}
        sx={{
          mt: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {base64 ? (
          <>
            <Button
              variant="contained"
              onClick={() => setBase64("")}
              sx={{
                bgcolor: "#a0a0a0",
                width: 130,
                height: 45,
                fontSize: 16,
                fontWeight: 700,
                mr: 1,
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() =>
                downloadBase64File(
                  base64,
                  `${boardName}.${format.toLowerCase()}`,
                  format.toLowerCase()
                )
              }
              sx={{
                bgcolor: "#008c23",
                width: 160,
                height: 45,
                fontSize: 16,
                fontWeight: 700,
              }}
              endIcon={<DownloadIcon style={{ fontSize: 23 }} />}
            >
              Download
            </Button>
          </>
        ) : (
          <Tooltip
            title={
              hasActivePlan
                ? ""
                : "Export is disabled because you do not have an active plan"
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={handleExport}
                disabled={exportDisabled}
                sx={{
                  bgcolor: exportDisabled ? "#a0a0a0" : "#008c23",
                  width: 300,
                  height: 45,
                  fontSize: 19,
                  fontWeight: 800,
                }}
                endIcon={
                  loading ? <></> : <FaFileExport style={{ fontSize: 23 }} />
                }
              >
                {loading ? <CircularProgress size={25} sx={{ color: "#008c23" }} /> : "Export"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Grid>
    </MotionGrid>
  );
}
